import { Injectable } from '@angular/core';
import { filterNilValue, QueryEntity } from '@datorama/akita';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AuthQuery } from '../auth';
import { Message, MessageQuery } from '../message';
import { RequestQuery } from '../request';
import { UserQuery } from '../user';
import { GroupState, GroupStore } from './group.store';

@Injectable()
export class GroupQuery extends QueryEntity<GroupState> {
  groups$ = combineLatest([
    this.selectAll(),
    this.messageQuery.selectAll(),
    this.requestQuery.selectAll(),
    this.authQuery.user$,
  ]).pipe(
    map(([groups = [], messages = [], requests = [], user]) => {
      const sortedMessages = messages.sort(
        (b, a) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const sortedMessageMap = sortedMessages.reduce(
        (m, message) => ({ ...m, [message.groupId]: message }),
        {} as { [key: string]: Message }
      );
      const lastMessageDateByGroupId = !sortedMessages?.length
        ? {}
        : sortedMessageMap;

      return groups
        .filter(({ users }) => users.some(({ id }) => id === user.id))
        .sort((groupA, groupB) => {
          const lmfgA = lastMessageDateByGroupId[groupA.id];
          const lmfgB = lastMessageDateByGroupId[groupB.id];

          const timeA = new Date(
            (lmfgA && lmfgA.createdAt) || groupA.createdAt
          ).getTime();
          const timeB = new Date(
            (lmfgB && lmfgB.createdAt) || groupB.createdAt
          ).getTime();
          return timeB - timeA;
        })
        .map((group) => {
          const lastMessage = lastMessageDateByGroupId[group.id];
          const hasUnread =
            user &&
            lastMessage &&
            !lastMessage.usersRead?.includes(user.id) &&
            lastMessage.createdBy !== user.id;
          return {
            ...group,
            lastMessage,
            hasUnread,
            requests: requests.filter(
              (r) => r.requestedForDocumentId === group.id
            ),
          };
        });
      // this.selectAll().pipe(
      //   map((groups) =>
      //     groups.sort((a, b) => {
      //       const am = moment(a.lastMessageReceivedAt || a.createdAt);
      //       const bm = moment(b.lastMessageReceivedAt || b.createdAt);

      //       return bm.valueOf() - am.valueOf();
      //     })
      //   )
      // );
    })
  );

  active$ = this.selectActive().pipe(
    filterNilValue(),
    switchMap((group) =>
      this.selectGroupRequests(group.id).pipe(
        map((requests) => ({ ...group, requests }))
      )
    )
  );

  count$ = this.selectAll().pipe(map((groups) => groups.length));

  private _combinedData$ = this.selectAll().pipe(
    switchMap((groups) =>
      combineLatest(
        groups
          .filter((group) => !!group)
          .map((group) =>
            this.selectGroupMessages(group.id).pipe(
              map((messages) => ({ group, messages }))
            )
          )
      )
    )
  );

  hasUnreadGroup$ = this._combinedData$.pipe(
    map((dataSet) => {
      const { user } = this.authQuery.getValue();
      return dataSet.some(({ group, messages }) => {
        const lastMessage = messages[0];
        if (!lastMessage) {
          return false;
        }
        // TODO: get it from messages query
        const userStat = null; // room.userStats.find(({ userId }) => userId === user.id);
        if (userStat) {
          return false; // userStat.lastReadMessageId !== lastMessage.id;
        }
        return false;
      });
    })
  );

  unreadGroups$ = this._combinedData$.pipe(
    map((dataSet) => {
      const { user } = this.authQuery.getValue();
      return dataSet.filter(({ group, messages }) => {
        const lastMessage = messages[0];
        if (!lastMessage) {
          return false;
        }
        // TODO: get it from messages query
        const userStat = null; // room.userStats.find(({ userId }) => userId === user.id);
        if (userStat) {
          return false; // userStat.lastReadMessageId !== lastMessage.id;
        }
        return false;
      });
    })
  );

  activeGroupMessages$: Observable<Message[]> = this.selectActiveId().pipe(
    switchMap((groupId) =>
      this.messageQuery.selectAll().pipe(
        map((messages) =>
          messages
            .filter((m) => m.groupId === groupId)
            .map((message) => ({
              ...message,
            }))
        )
      )
    )
  );

  constructor(
    protected store: GroupStore,
    private authQuery: AuthQuery,
    private messageQuery: MessageQuery,
    private userQuery: UserQuery,
    private requestQuery: RequestQuery
  ) {
    super(store);
  }

  selectGroupMessages(id: string) {
    return this.messageQuery
      .selectAll()
      .pipe(
        map(
          (messages) =>
            messages.filter((m) => m.groupId === id) as unknown as Message[]
        )
      );
  }
  getLastMessage(id: string) {
    return this.messageQuery.selectAll().pipe(
      map((messages) => {
        const [lastMessage] = messages
          .filter((message) => message.groupId === id)
          .reverse();
        if (!lastMessage) {
          return of(null);
        }
        return lastMessage;
      })
    );
  }

  selectGroupRequests(id: string) {
    return this.requestQuery
      .selectAll()
      .pipe(
        map((requests) =>
          requests.filter((r) => r.requestedForDocumentId === id)
        )
      );
  }
}
